import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  List,
  ListItem,
  ListItemButton,
  Radio,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import VuiButton from "components/VuiButton";
import { selectedMerchant as setSelectedMerchant } from "redux/actions";
import { env } from "configs/EnvironmentConfig";
import { useApi } from "hooks/useAPI";
import AppService from "../../services/AppService";

const { QUICKBOOKS_CLIENT_ID, QUICKBOOKS_REDIRECT_URI, QUICKBOOKS_AUTH_URL, QUICKBOOKS_SCOPE } = env;

const QuickbooksConnect = ({ merchant, title }) => {
  const { getToken } = useApi();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [accountIds, setAccountIds] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [search, setSearch] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOk = () => setShowModal(false);
  const handleCancel = () => setShowModal(false);

  const initiateQuickbooksAuth = () => {
    setLoading(true);

    const scope = QUICKBOOKS_SCOPE;
    const state = Math.random().toString(36).substring(2, 15);
    const authUrl = QUICKBOOKS_AUTH_URL;

    const url = new URL(authUrl);
    url.searchParams.append("client_id", QUICKBOOKS_CLIENT_ID);
    url.searchParams.append("response_type", "code");
    url.searchParams.append("scope", scope);
    url.searchParams.append("redirect_uri", QUICKBOOKS_REDIRECT_URI);
    url.searchParams.append("state", state);

    localStorage.setItem("quickbooks_auth_state", state);

    window.location.href = url.toString();
  };

  const addQuickbooksAccountId = async () => {
    try {
      const data = {
        merchantId: merchant?.id,
        quickbooksAccountId: accountId,
      };
      const token = getToken ? await getToken() : "";
      const apiResponse = await AppService.quickbooksInstall(data, token);

      dispatch(setSelectedMerchant({
        ...apiResponse,
        roles: merchant.roles
      }));

      enqueueSnackbar("QuickBooks account added!", { variant: "success" });
      handleOk();
    } catch (error) {
      enqueueSnackbar("Failed to add QuickBooks account", { variant: "error" });
    }
  };

  // Search functionality for the account selection modal
  const onSearch = (query) => {
    if (query !== "") {
      query = query.toLowerCase();
      const filter = accountIds.filter(
        (item) =>
          item?.name?.toLowerCase().includes(query) ||
          item?.id?.toLowerCase().includes(query)
      );
      setSearch(filter);
    } else {
      setSearch([]);
    }
  };

  return (
    <>
      <VuiButton 
        fullWidth
        variant="contained" 
        loading={loading}
        onClick={initiateQuickbooksAuth}
      >
        {title}
      </VuiButton>

      <Dialog
        open={showModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ color: "white !important" }}>
          Select a QuickBooks Account
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {accountIds?.length > 0 && (
              <TextField
                fullWidth
                placeholder="Search by name or account id"
                onChange={(e) => onSearch(e.target.value)}
                sx={{
                  input: { color: "white" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
                    "&:hover fieldset": { borderColor: "white" },
                  },
                }}
              />
            )}
            <List sx={{ width: "100%" }}>
              {(search.length > 0 ? search : accountIds).map((account) => (
                <ListItem key={account.id} disablePadding>
                  <ListItemButton
                    onClick={() => setAccountId(account.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Radio
                      checked={accountId === account.id}
                      sx={{
                        color: "white !important",
                      }}
                    />
                    <Stack>
                      <strong style={{ color: "white" }}>{account.name}</strong>
                      <span style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                        {account.id}
                      </span>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <VuiButton variant="outlined" onClick={handleCancel}>
            Cancel
          </VuiButton>
          <VuiButton
            variant="contained"
            disabled={!accountId}
            onClick={addQuickbooksAccountId}
          >
            OK
          </VuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickbooksConnect;
